import { findIndex, slice, truncate } from 'lodash';

export const MAX_LENGTH = 100;
export const SEARCH_RANGE = 20; // Characters to search for safe break point
export const SAFE_BREAK_PATTERN = /[\s.,:;!?)]/; // Pattern for characters that are safe to break text at: whitespace, punctuation, closing parenthesis
export const HTML_TAG_PATTERN = /<[^>]*>/g; // Pattern to match and remove HTML tags

/**
 * Finds the index where to cut HTML text while avoiding breaking HTML tags.
 * Counts only visible characters (not inside HTML tags) up to MAX_LENGTH.
 * @param text - The HTML string to analyze
 * @returns Index where the text should be cut, or -1 if no cut needed
 */
const findHTMLCutPoint = (text: string): number => {
    let charCount = 0;
    let inTag = false;

    for (let i = 0; i < text.length; i++) {
        const char = text[i];

        if (char === '<') {
            inTag = true;
        } else if (char === '>') {
            inTag = false;
        } else if (!inTag) {
            charCount++;
            if (charCount >= MAX_LENGTH) {
                return i;
            }
        }
    }

    return -1;
};

/**
 * Finds a natural break point near the given index to avoid cutting words mid-way.
 * Searches within SEARCH_RANGE for whitespace or punctuation characters.
 * @param text - The text to search in
 * @param startIndex - Starting position to search from
 * @returns Index of a safe break point, or original startIndex if none found
 */
const findSafeBreakPoint = (text: string, startIndex: number): number => {
    const searchRange = slice(text, startIndex, startIndex + SEARCH_RANGE);
    const breakPointIndex = findIndex(searchRange, (char) => SAFE_BREAK_PATTERN.test(char));

    if (breakPointIndex === -1) {
        return startIndex;
    }

    const char = searchRange[breakPointIndex];
    // If the break character is a space, do not include it in the truncated result (for cleaner output).
    // If it's punctuation, include it for readability.
    return startIndex + breakPointIndex + (char === ' ' ? 0 : 1);
};

interface TruncateContentParams {
    description: string;
    isExpanded: boolean;
    shouldTruncate: boolean;
}

/**
 * Truncates content intelligently, respecting HTML structure and word boundaries.
 * Returns the full content if expanded or truncation is disabled.
 *
 * @param params - Configuration for content truncation
 * @returns Truncated content string or original if no truncation needed
 */
export const getTruncatedContent = ({ description, isExpanded, shouldTruncate }: TruncateContentParams): string => {
    if (!shouldTruncate || isExpanded) {
        return description;
    }

    const isHTML = HTML_TAG_PATTERN.test(description);
    if (isHTML) {
        const htmlIndex = findHTMLCutPoint(description);
        if (htmlIndex === -1) {
            return description;
        }

        const cutPoint = findSafeBreakPoint(description, htmlIndex);
        return description.substring(0, cutPoint).trim();
    }

    return truncate(description, {
        length: MAX_LENGTH,
        separator: /[,;.]?\s+/, // Break at punctuation followed by whitespace
        omission: '', // No ellipsis since we handle "Show more" separately
    });
};
